/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, September 22nd 2021, 11:44:01 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { ThemeProvider, createTheme } from "@mui/material";
export { ThemeProvider };

export const enum RGBTriplets {
  primary = "123, 0, 177",
  secondary = "255, 192, 203",
}

export const setCSSRootComputedStyle = (property: string, value: string) => {
  document.documentElement.style.setProperty(property, value);
};

export const GRADIENTCOLORSCHEME = {
  color1: "rgba(204, 0, 153, 1)",
  color2: `rgb(113 0 199)`,
};

const DEFAULTGRADIENT =
  "linear-gradient( 4deg, " + GRADIENTCOLORSCHEME.color1 + " -20%, " + GRADIENTCOLORSCHEME.color2 + " 60% ";

/**
 * Initializing the css properties. These will be set from an API call to organizations later on. Organizations will
 * have color properties for the brand.
 *
 */

setCSSRootComputedStyle("--primary", RGBTriplets.primary);
setCSSRootComputedStyle("--primaryVariant", `rgba(${RGBTriplets.primary}, 50%)`);
setCSSRootComputedStyle("--secondary", "#CC0099");
setCSSRootComputedStyle("--secondaryVariant", "#CC009980");
setCSSRootComputedStyle("--surface", `rgba(${RGBTriplets.primary})`);
setCSSRootComputedStyle("--background", "#ffffff");
setCSSRootComputedStyle("--error", "#f44336");
setCSSRootComputedStyle("--info", "#29b6f6");
setCSSRootComputedStyle("--warning", "#ffa500");
setCSSRootComputedStyle("--success", "#33b86c");
setCSSRootComputedStyle("--onPrimary", "#FFFFFF");
setCSSRootComputedStyle("--onSecondary", "#FFFFFF");
setCSSRootComputedStyle("--onSurface", "#FFFFFF");
setCSSRootComputedStyle("--onSecondary", "#FFFFFF");
setCSSRootComputedStyle("--onSecondary", `rgba(${RGBTriplets.primary})`);
setCSSRootComputedStyle("--onBackground", `rgba(${RGBTriplets.primary})`);
setCSSRootComputedStyle("--onError", "#FFFFFF");
setCSSRootComputedStyle("--default-gradient-background", "rgba(0, 0, 255, 1)");
setCSSRootComputedStyle("--default-gradient", DEFAULTGRADIENT);

export const COLORSCHEME = {
  primary: `rgba(${RGBTriplets.primary})`,
  primaryVariant: `rgba(${RGBTriplets.primary}, 50%)`,
  secondary: "#CC0099",
  secondaryVariant: "#CC009980",
  surface: `rgba(${RGBTriplets.primary})`,
  background: "#ffffff",
  error: "#f44336",
  info: "#29b6f6",
  warning: "#ffa500",
  success: "#33b86c",
  onPrimary: "#FFFFFF",
  onSecondary: "#FFFFFF",
  onSurface: `rgba(${RGBTriplets.primary})`,
  onBackground: `rgba(${RGBTriplets.primary})`,
  onError: "#FFFFFF",
  defaultGradient: DEFAULTGRADIENT,
};

export const enum OSDIMENSIONS {
  drawerWidth = 240,
}

// Most of the ThemeData can be overridden by css/Core.css that has the highest prioty

export const osMUITheme = createTheme({
  palette: {
    primary: {
      main: COLORSCHEME.primary,
      light: COLORSCHEME.primary,
      dark: COLORSCHEME.primary,
      contrastText: COLORSCHEME.onPrimary,
    },
    secondary: {
      main: COLORSCHEME.secondary,
      light: COLORSCHEME.secondary,
      dark: COLORSCHEME.secondaryVariant,
      contrastText: COLORSCHEME.onSecondary,
    },
    error: {
      main: COLORSCHEME.error,
      light: COLORSCHEME.error,
      dark: COLORSCHEME.error,
      contrastText: COLORSCHEME.onPrimary,
    },
    warning: {
      main: COLORSCHEME.warning,
      light: COLORSCHEME.warning,
      dark: COLORSCHEME.warning,
      contrastText: COLORSCHEME.onPrimary,
    },
    info: {
      main: COLORSCHEME.info,
      light: COLORSCHEME.info,
      dark: COLORSCHEME.info,
      contrastText: COLORSCHEME.onPrimary,
    },
    success: {
      main: COLORSCHEME.success,
      light: COLORSCHEME.success,
      dark: COLORSCHEME.success,
      contrastText: COLORSCHEME.onPrimary,
    },
  },
  typography: {
    fontSize: 12,
    fontFamily: [
      "Source Sans Pro",
      "-apple-system",
      "BlinkMacSystemFont",
      "Segoe UI",
      "Roboto",
      "Helvetica Neue",
      "Arial",
      "sans-serif",
      "Apple Color Emoji",
      "Segoe UI Emoji",
      "Segoe UI Symbol",
    ].join(","),
  },
  components: {
    MuiButton: {
      defaultProps: {
        color: "primary",
        variant: "contained",
        size: "small",
      },
      styleOverrides: {
        // Name of the slot
        root: {
          // Some CSS.
        },
      },
    },
  },
});
